<template>
  <div class="top-bar">
    <button v-if="!isLogin" class="login-button" @click="handleLoginClick">
      登录
    </button>

    <div v-else class="user-container">
      <div class="cloud-space-wrapper">
        <button class="cloud-space-button" @click="handleCloudSpaceClick">
          云空间
        </button>
        <div class="file-badge">+{{ 1 }}</div>
      </div>
      <div class="avatar-wrapper" ref="dropdownRef">
        <img
          class="avatar"
          :class="{ 'is-loading': isLoading }"
          :src="avatarUrl"
          @click="toggleDropdown"
          alt="用户头像"
        />
        <div v-if="showDropdown" class="dropdown-menu">
          <button
            class="menu-item"
            @click="handleChangeAvatar"
            :disabled="isLoading"
          >
            {{ isLoading ? "加载中..." : "切换头像" }}
          </button>
          <button class="menu-item" @click="handleLogout">退出登录</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useLogin } from "@/composables/login/useLogin";
import { useUserInfo } from "@/composables/user/useUserInfo";
import { useRouter } from "vue-router";
import Tracking from "@/model/tracking.js";

const router = useRouter();
const showDropdown = ref(false);
const dropdownRef = ref(null);

const {
  state: { isLogin },
  action: { openLoginDialog, logout },
} = useLogin();

const {
  state: { isLoading },
  action: { generateNewAvatar, getAvatarUrl },
} = useUserInfo();

const avatarUrl = computed(() => getAvatarUrl());

const handleCloudSpaceClick = () => {
  Tracking.send({
    trigger: "button-click",
    page: "main-page",
    object: "cloud-space-btn",
  });
  router.push("/cloudspace/files");
};

const handleLoginClick = () => {
  Tracking.send({
    trigger: "button-click",
    page: "main-page",
    object: "login-btn",
  });
  openLoginDialog();
};

const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value;
  if (showDropdown.value) {
    Tracking.send({
      trigger: "button-click",
      page: "main-page",
      object: "avatar-dropdown-btn",
    });
  }
};

const handleLogout = async () => {
  Tracking.send({
    trigger: "button-click",
    page: "main-page",
    object: "logout-btn",
  });
  await logout();
  showDropdown.value = false;
};

const handleChangeAvatar = () => {
  Tracking.send({
    trigger: "button-click",
    page: "main-page",
    object: "change-avatar-btn",
  });
  generateNewAvatar();
  showDropdown.value = false;
};

// 处理点击外部关闭下拉菜单
const handleClickOutside = (event) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    showDropdown.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<style scoped lang="scss">
.top-bar {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: px2rem(64);
  z-index: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.login-button {
  width: px2rem(84);
  height: px2rem(30);
  background: transparent;
  border: px2rem(1) solid #2d7f6f;
  border-radius: px2rem(12);
  cursor: pointer;
  color: #ffffff;
  font-size: px2rem(13);
  font-family: "思源黑体 Medium";
  line-height: px2rem(19.5);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: px2rem(20);

  &:hover {
    transition: all 0.15s linear;
    box-shadow: 0 0 px2rem(20) rgba(45, 127, 111, 0.5);
  }
}

.user-container {
  display: flex;
  align-items: center;
  gap: px2rem(40);
  margin-right: px2rem(20);

  @media screen and (max-width: 768px) {
    gap: px2rem(28);
  }

  .cloud-space-button {
    width: px2rem(97);
    height: px2rem(30);
    background: transparent;
    border: px2rem(1) solid #2d7f6f;
    border-radius: px2rem(12);
    cursor: pointer;
    color: #ffffff;
    font-size: px2rem(13);
    font-family: "思源黑体 Medium";
    line-height: px2rem(19.5);
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      transition: all 0.15s linear;
      box-shadow: 0 0 px2rem(20) rgba(45, 127, 111, 0.5);
    }
  }

  .avatar-wrapper {
    display: flex;
    align-items: center;
    position: relative;
  }

  .avatar {
    width: px2rem(32);
    height: px2rem(32);
    border-radius: px2rem(12);
    cursor: pointer;
    background: #ffffff;
    object-fit: cover;
    border: px2rem(2) solid rgba(45, 127, 111, 0.2);
    box-shadow: inset 0 0 px2rem(2) rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;

    &.is-loading {
      opacity: 0.6;
      cursor: wait;
      animation: pulse 1.5s ease-in-out infinite;
    }

    &:hover:not(.is-loading) {
      border-color: rgba(45, 127, 111, 0.4);
      box-shadow: 0 0 px2rem(20) rgba(45, 127, 111, 0.3),
        inset 0 0 px2rem(2) rgba(0, 0, 0, 0.1);
      transform: translateY(-1px);
    }

    &:active:not(.is-loading) {
      transform: translateY(0);
    }
  }
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + px2rem(8));
  right: 0;
  background: #262626;
  border-radius: px2rem(12);
  padding: px2rem(8);
  z-index: 10;
  min-width: px2rem(120);
  box-shadow: 0 px2rem(4) px2rem(12) rgba(0, 0, 0, 0.15);

  .menu-item {
    width: 100%;
    height: px2rem(32);
    background: transparent;
    border: none;
    color: #ffffff;
    font-size: px2rem(13);
    font-family: "思源黑体 Regular";
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: px2rem(6);
    transition: all 0.2s ease;
    user-select: none;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    &:disabled {
      opacity: 0.6;
      cursor: wait;
    }
  }
}

@keyframes pulse {
  0% {
    opacity: 0.6;
  }
  50% {
    opacity: 0.3;
  }
  100% {
    opacity: 0.6;
  }
}

.cloud-space-wrapper {
  position: relative;
  display: inline-block;
}

.file-badge {
  display: none;
  cursor: pointer;
  position: absolute;
  bottom: -6px;
  right: -6px;
  background: #007fff;
  color: white;
  border-radius: 12px;
  padding: 3px 8px;
  font-size: 12px;
  line-height: 1;
  font-family: "思源黑体 Medium";
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(0, 127, 255, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  transform: scale(0.9);
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1);
    box-shadow: 0 4px 12px rgba(0, 127, 255, 0.4);
  }
}
</style>
